import {
  Loading,
  SimpleShowLayout,
  TextField,
  useGetOne,
  useRedirect,
  DateField,
  FunctionField,
  BooleanField,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { AdminSessionResponseDto, AdminSessionResponseDtoStatusEnum, AdminSessionResponseDtoTypeEnum } from 'shared/api';
import { Chip } from '@mui/material';

export const SessionShow = () => {
  const { id } = useParams<{ id: any }>();
  const redirect = useRedirect();

  const { data: sessionData, isLoading: isSessionLoading, error: sessionError } = useGetOne<AdminSessionResponseDto>(
    'sessions',
    { id },
    { onError: () => redirect('/sessions') },
  );

  if (isSessionLoading) {
    return <Loading />;
  }

  if (sessionError) {
    return (
      <p>
        Произошла ошибка при загрузке данных:
      </p>
    );
  }

  if (!sessionData) {
    return <p>Данные отсутствуют.</p>;
  }

  // Вспомогательная функция для отображения статуса сессии с подходящим цветом
  const renderSessionStatus = (record: AdminSessionResponseDto) => {
    const statusColors = {
      [AdminSessionResponseDtoStatusEnum.Awaiting]: 'warning',
      [AdminSessionResponseDtoStatusEnum.Live]: 'info',
      [AdminSessionResponseDtoStatusEnum.Complete]: 'success',
      [AdminSessionResponseDtoStatusEnum.Canceled]: 'error',
    };
    
    const statusLabels = {
      [AdminSessionResponseDtoStatusEnum.Awaiting]: 'Ожидание',
      [AdminSessionResponseDtoStatusEnum.Live]: 'В процессе',
      [AdminSessionResponseDtoStatusEnum.Complete]: 'Завершена',
      [AdminSessionResponseDtoStatusEnum.Canceled]: 'Отменена',
    };

    return (
      <Chip 
        label={statusLabels[record.status] || record.status} 
        color={statusColors[record.status] as any || 'default'} 
        variant="outlined" 
      />
    );
  };

  return (
    <SimpleShowLayout>
      <TextField source="id" label="ID сессии" />
      <TextField source="therapistId" label="ID терапевта" />
      
      <FunctionField 
        label="Статус сессии" 
        render={(record: AdminSessionResponseDto) => renderSessionStatus(record)} 
      />
      
      <FunctionField 
        label="Тип сессии"
        render={(record: AdminSessionResponseDto) => 
          record.type === AdminSessionResponseDtoTypeEnum.Personal ? 'Индивидуальная' : 'Групповая'
        }
      />
      
      <DateField 
        source="availability.startWith" 
        label="Время начала"
        showTime
        options={{ 
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }}
      />
      
      <DateField 
        source="availability.endsWith" 
        label="Время окончания"
        showTime
        options={{ 
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }}
      />
      
      <TextField source="payment.value" label="Стоимость сессии" />
      <TextField source="payment.discountValue" label="Размер скидки" />
      <TextField source="payment.currency" label="Валюта" />
      
      <FunctionField 
        label="Статус оплаты" 
        render={(record: AdminSessionResponseDto) => (
          <Chip 
            label={record.payment.status === 'succeeded' ? 'Оплачено' : record.payment.status} 
            color={record.payment.status === 'succeeded' ? 'success' : 'default'} 
            variant="outlined" 
          />
        )} 
      />
      
      <BooleanField source="isTherapistVisited" label="Терапевт присутствовал" />
      
      {sessionData.cancelerId && (
        <>
          <TextField source="cancelerId" label="ID отменившего сессию" />
          <TextField source="cancelReason" label="Причина отмены" />
        </>
      )}
      
      <FunctionField 
        label="Пациенты" 
        render={(record: AdminSessionResponseDto) => (
          <div>
            {record.patients.map(patient => (
              <div key={patient.id}>
                {patient.firstname} {patient.lastname} (ID: {patient.id})
              </div>
            ))}
          </div>
        )} 
      />
      
      <BooleanField source="payment.isPromocodePaid" label="Промокод выплачен" />
    </SimpleShowLayout>
  );
};